import React, { useState } from 'react'
import { Space, Switch } from 'antd'
import { FileUploader } from '@pushly/aqe/lib/components'
import { simpleNotification } from '../../_utils/utils'

interface SegmentIconFieldProps {
    level: 'domain' | 'org'
    ownerId: number
    mediaQuality?: number
    /** The icon the segment currently carries; null when it uses the default. */
    iconUrl: string | null
    readonly: boolean
    onChange: (iconUrl: string | null) => void
}

/**
 * Lets a segment replace the domain's default notification icon whenever it is the only segment a
 * notification targets.
 *
 * The switch is local state seeded from whether an icon is already stored, because "override on,
 * nothing uploaded yet" is a state the segment itself cannot express. Turning it off clears the icon
 * so a stored value never survives behind a switch that reads off.
 */
export const SegmentIconField = ({
    level,
    ownerId,
    mediaQuality,
    iconUrl,
    readonly,
    onChange,
}: SegmentIconFieldProps) => {
    const [overrideEnabled, setOverrideEnabled] = useState(!!iconUrl)

    return (
        <Space vertical className="segment-query-builder-icon-field">
            <label>
                <Switch
                    size="small"
                    checked={overrideEnabled}
                    disabled={readonly}
                    onChange={(enabled) => {
                        if (!enabled) onChange(null)
                        setOverrideEnabled(enabled)
                    }}
                />
                <span className="label-with-component-l">Override default icon when targeting this segment.</span>
            </label>

            {overrideEnabled && (
                <FileUploader
                    type="image"
                    buttonText="Icon"
                    disabled={readonly}
                    ownerType={level}
                    ownerId={ownerId}
                    defaultValue={iconUrl ? { url: iconUrl } : undefined}
                    imageConfig={{
                        resize: FileUploader.defaultDimensions.notificationIcon,
                        cropMax: FileUploader.defaultDimensions.notificationIcon,
                        cropRatio: 1,
                        quality: mediaQuality,
                    }}
                    onSuccess={(iconUpload) => onChange(iconUpload.url)}
                    onRemove={() => onChange(null)}
                    onError={(res: any) => {
                        const error = res?.error
                        if (error && error.error_type === 'invalid_file_type_error') {
                            return simpleNotification('error', error.message)
                        }
                    }}
                />
            )}
        </Space>
    )
}
